import { isNil } from 'lodash';
import {
  ENTERPRISE_VALUE_ROW_NUMBER,
  EQUITY_VALUE_ROW_NUMBER,
  INPUTS_TABLE_ALIASES,
} from 'common/constants/calibration';
import { Cells } from 'common/types/scalarSpreadsheet';
import { Calibration } from 'common/types/valuation';
import { CustomParserParamCalibration } from 'pages/Valuations/approaches/guidelinePublicCompanies/PerformanceMetrics/config/customParser/types';
import { PUBLIC_COMPANIES } from 'pages/Valuations/util/constants';
import {
  VALUATION_SUMMARY_SPREADSHEET_LESS_CASH,
  VALUATION_SUMMARY_SPREADSHEET_PLUS_DEBT,
} from 'pages/ValuationsAllocation/common/constants/valuationSummary/sheetAliases';
import { getDefaultCompsApproachForDropdown } from 'pages/ValuationsAllocation/util/getDefaultCompsApproachForDropdown';
import { getObjectValue, gridShortDate } from 'utilities';
import { alphabetGenerator } from 'utilities/alphabet-utilities';
import { getFinalValueByAlias, getPlusDebtAndLessCashInputsTable } from '../utils';

const parser = async (params: CustomParserParamCalibration) => {
  const {
    EQUITY_VALUE_INPUTS,
    ENTERPRISE_VALUE_INPUTS,
    CALIBRATION_DATE,
    LTM_REVENUE_INPUTS,
    NTM_REVENUE_INPUTS,
    LTM_EBITDA_INPUTS,
    NTM_EBITDA_INPUTS,
    FINANCIALS_VERSION,
  } = INPUTS_TABLE_ALIASES;
  const { columns, rowConfig, tableData } = params;
  let cells = {} as Cells;
  const { calibration, isDisabled, approaches } = tableData;
  const defaultApproach = getDefaultCompsApproachForDropdown(approaches ?? [], [PUBLIC_COMPANIES]);
  const calibrationData: Calibration = {
    ...calibration,
  };
  if (
    defaultApproach &&
    calibrationData.public_comps_approach_calibration === null &&
    isNil(calibrationData.temp_public_comps_reference)
  ) {
    const propToModify = defaultApproach.id ? 'public_comps_approach_calibration' : 'temp_public_comps_reference';
    calibrationData[propToModify] = defaultApproach.id ?? defaultApproach.panel_id ?? null;
  }
  const alphabet = alphabetGenerator([], columns.length);
  const columnsLength = columns.length;

  columns.forEach((column, columnIndex: number) => {
    const columnLegend = alphabet[columnIndex];

    rowConfig.forEach((row, rowIndex: number) => {
      const rowNumber = rowIndex + 1;
      const key = columnLegend + rowNumber;
      const colNumber = columnIndex + 1;
      const isLastColumn = colNumber === columnsLength;
      const { alias, className } = getObjectValue(row);
      let { expr, exprPair, gridType, readOnly, rowNumberPair, tooltipMessages } = row;

      let value = getFinalValueByAlias(column[alias]?.value ?? '', gridType, calibrationData, alias);
      const { format } = row;

      const { style } = column;

      const enterpriseValueExpr = `=${EQUITY_VALUE_INPUTS} + ${VALUATION_SUMMARY_SPREADSHEET_PLUS_DEBT} - ${VALUATION_SUMMARY_SPREADSHEET_LESS_CASH} `;
      const equityValueExpr = `=${ENTERPRISE_VALUE_INPUTS} - ${VALUATION_SUMMARY_SPREADSHEET_PLUS_DEBT} + ${VALUATION_SUMMARY_SPREADSHEET_LESS_CASH}`;

      switch (alias) {
        case CALIBRATION_DATE:
          value = gridShortDate(value);
          break;
        case VALUATION_SUMMARY_SPREADSHEET_LESS_CASH:
          ({ gridType, readOnly, value } = getPlusDebtAndLessCashInputsTable(
            isDisabled,
            isLastColumn,
            gridType,
            calibrationData,
            readOnly,
            value,
            false
          ));
          break;
        case VALUATION_SUMMARY_SPREADSHEET_PLUS_DEBT:
          ({ gridType, readOnly, value } = getPlusDebtAndLessCashInputsTable(
            isDisabled,
            isLastColumn,
            gridType,
            calibrationData,
            readOnly,
            value,
            true
          ));
          break;
        case ENTERPRISE_VALUE_INPUTS:
          expr = enterpriseValueExpr;
          exprPair = enterpriseValueExpr;
          tooltipMessages = ['This cell has been calculated automatically'];
          rowNumberPair = EQUITY_VALUE_ROW_NUMBER;
          break;
        case EQUITY_VALUE_INPUTS:
          exprPair = equityValueExpr;
          rowNumberPair = ENTERPRISE_VALUE_ROW_NUMBER;
          break;
        case LTM_REVENUE_INPUTS:
        case NTM_REVENUE_INPUTS:
        case LTM_EBITDA_INPUTS:
        case NTM_EBITDA_INPUTS:
          if (Number(calibrationData.financials_version) === 0 && !isDisabled) {
            readOnly = false;
          }
          break;
        case FINANCIALS_VERSION:
          if (value === '') {
            value = 0;
          }
          break;
        default:
          break;
      }

      const cell = {
        alias: row.alias || '',
        className,
        columnId: column.id,
        columnLegend,
        columnOrder: column.order,
        customFormat: column.customFormat || null,
        customKey: alias,
        dbType: 'string',
        expr,
        exprPair,
        format,
        gridType,
        ignoreAutoScroll: row.ignoreAutoScroll,
        isGpcRow: false,
        isNotNavigable: row.isNotNavigable,
        isParent: column.isParent,
        key,
        parentColumn: column.parentColumn,
        readOnly,
        rowNumber,
        rowNumberPair,
        style,
        tooltipMessages,
        value,
      };

      cells = {
        ...cells,
        [key]: cell,
      };
    });
  });

  return cells;
};

export default parser;
